/**
 * Save-to-disk helper.
 *
 * Uses the File System Access API (Chrome, Edge, Opera) when available:
 * - Once, user picks a "Default Save Folder" via showDirectoryPicker()
 * - We store the returned FileSystemDirectoryHandle in IndexedDB
 * - Subsequent downloads write directly into that folder with our filename,
 *   no dialog per file.
 *
 * Fallback for Firefox / Safari: creates an <a download> link. The file
 * lands in whatever the user set as their browser's download folder.
 */
import { api, API } from "@/lib/api";

const DB_NAME = "rcs-save-config";
const STORE = "handles";
const KEY = "default-dir";

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const rq = tx.objectStore(STORE).get(key);
    rq.onsuccess = () => resolve(rq.result || null);
    rq.onerror = () => reject(rq.error);
  });
}

async function idbSet(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbDel(key) {
  const db = await openDb();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
  });
}

export function supportsFSApi() {
  return typeof window !== "undefined" && "showDirectoryPicker" in window;
}

export async function pickDefaultFolder() {
  if (!supportsFSApi()) {
    throw new Error("Your browser doesn't support choosing a save folder. Firefox and Safari save to the folder configured in your browser's download settings.");
  }
  const handle = await window.showDirectoryPicker({ mode: "readwrite" });
  await idbSet(KEY, handle);
  return handle;
}

export async function clearDefaultFolder() {
  await idbDel(KEY);
}

export async function getDefaultFolderName() {
  const h = await idbGet(KEY);
  if (!h) return null;
  return h.name;
}

async function verifyRWPermission(handle, promptIfNeeded = true) {
  if (await handle.queryPermission({ mode: "readwrite" }) === "granted") return true;
  if (!promptIfNeeded) return false;
  return (await handle.requestPermission({ mode: "readwrite" })) === "granted";
}

// Sanitise arbitrary text to something safe for a filesystem.
function safe(s) {
  return String(s || "")
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, "")   // illegal on Windows/mac/nix
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
}

/**
 * Build the detailed filename from a clip's metadata + parent event.
 * Format: "{Event} - {Header}[ & Heeler] - {Horse} - {Category} - {Sub-label} - {Notes}.mp4"
 */
export function buildClipFilename(clip, eventName = "") {
  const parts = [];
  const ev = safe(eventName);
  if (ev) parts.push(ev);

  const header = safe(clip.rider_horse_name);
  const heeler = safe(clip.heeler_name);
  const riderPart = header && heeler ? `${header} & ${heeler}` : (header || heeler);
  if (riderPart) parts.push(riderPart);

  const horse = safe(clip.horse_name);
  if (horse) parts.push(horse);

  const cat = safe(clip.category);
  if (cat) parts.push(cat);

  const sub = safe(clip.sub_label);
  if (sub) parts.push(sub);

  const notes = safe(clip.notes);
  if (notes && notes.length > 0 && notes.length < 40) parts.push(notes);

  const name = (parts.join(" - ") || "clip").slice(0, 180);
  return `${name}.mp4`;
}

/**
 * Ensure the persisted default folder is picked AND has readwrite permission
 * NOW (inside a user gesture). Call this BEFORE launching a bulk save loop —
 * once permission is granted, subsequent `queryPermission` calls don't need
 * a gesture, so the loop can keep writing files even after `await` breaks
 * gesture context.
 *
 * Returns the granted directory handle, or null if the browser doesn't
 * support the API or the user cancelled the prompt.
 */
export async function ensureFolderPermission() {
  if (!supportsFSApi()) return null;
  let dir = await idbGet(KEY);
  if (!dir) {
    try {
      dir = await window.showDirectoryPicker({ mode: "readwrite" });
      await idbSet(KEY, dir);
    } catch {
      return null; // user cancelled
    }
  }
  const ok = await verifyRWPermission(dir, true);
  if (!ok) return null;
  return dir;
}

/**
 * Save one clip to disk. If `preAcquiredDir` is passed, uses that folder
 * without asking for permission again (needed for bulk loops). Otherwise
 * falls back to querying permission and, if missing, uses the browser
 * download path.
 * Returns { ok, filename, method }.
 */
export async function saveClipToDisk(clip, eventName = "", onProgress = null, preAcquiredDir = null) {
  const filename = buildClipFilename(clip, eventName);

  // FS-API fast path: previously picked folder is available and permission
  // is either already granted OR was acquired upfront by ensureFolderPermission
  if (supportsFSApi()) {
    try {
      const dir = preAcquiredDir || await idbGet(KEY);
      // In a loop we've already asked for permission — don't try to
      // re-prompt (would silently fail without a gesture). Just check.
      if (dir && (await verifyRWPermission(dir, !preAcquiredDir))) {
        // Stream response body straight into the FileHandle — no intermediate
        // blob, so memory footprint stays flat even with 12+ large clips.
        // Auth is cookie-based (see api.js withCredentials), so we must
        // include credentials on the fetch call.
        const streamUrl = `${API}/clips/${clip.id}/download`;
        const resp = await fetch(streamUrl, {
          credentials: "include",
        });
        if (!resp.ok) {
          throw new Error(`server returned ${resp.status}`);
        }
        const fh = await dir.getFileHandle(filename, { create: true });
        const writable = await fh.createWritable();
        const total = Number(resp.headers.get("Content-Length") || 0);
        let received = 0;
        const reader = resp.body.getReader();
        try {
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            await writable.write(value);
            received += value.byteLength;
            if (onProgress && total) onProgress(received / total);
          }
        } finally {
          await writable.close();
        }
        return { ok: true, filename, method: "fs-api-folder" };
      }
    } catch (e) {
      // Fall through to <a download> fallback with informative log
      console.warn("[saveToDisk] FS API path failed:", e);
    }
  }

  // Fallback path — <a download>. Loads the full blob into memory but is the
  // only option for Firefox / Safari, or when the FS-API folder isn't set.
  const res = await api.get(`/clips/${clip.id}/download`, {
    responseType: "blob",
    onDownloadProgress: (e) => {
      if (onProgress && e.total) onProgress(e.loaded / e.total);
    },
  });
  const blob = res.data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return { ok: true, filename, method: "browser-download" };
}
